import React from 'react';
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { Toaster } from './components/ui/toaster';
import ErrorBoundary from './components/ErrorBoundary';
import NotFound from './components/NotFound';

// Public Pages
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import FeaturesSection from './components/FeaturesSection';
import ComparisonSection from './components/ComparisonSection';
import FAQSection from './components/FAQSection';
import Footer from './components/Footer';

// Manager App
import Login from './components/manager/Login';
import ManagerLayout from './components/manager/ManagerLayout';
import Dashboard from './components/manager/Dashboard';
import Home from './components/manager/Home';
import PropertiesList from './components/manager/PropertiesList';
import TenantsList from './components/manager/TenantsList';
import MoneyManagement from './components/manager/MoneyManagement';
import Collections from './components/manager/Collections';
import Dues from './components/manager/Dues';
import LeadsList from './components/manager/LeadsList';
import Settings from './components/manager/Settings';
import ExpensesList from './components/manager/ExpensesList';
import BanksList from './components/manager/BanksList';
import ProfitLoss from './components/manager/ProfitLoss';
import RoomsList from './components/manager/RoomsList';
import TasksManagement from './components/manager/TasksManagement';
import Reports from './components/manager/Reports';
import RentAgreements from './components/manager/RentAgreements';
import PoliceVerifications from './components/manager/PoliceVerifications';
import PropertyDetails from './components/manager/PropertyDetails';
import Reminders from './components/manager/Reminders';
import ReminderSettings from './components/manager/ReminderSettings';
import TenantProfile from './components/manager/TenantProfile';
import DuesPackages from './components/manager/DuesPackages';
import BookingsList from './components/manager/BookingsList';
import TeamManagement from './components/manager/TeamManagement';

// Super Admin App
import AdminLogin from './components/admin/AdminLogin';
import AdminLayout from './components/admin/AdminLayout';
import OldTenants from './components/manager/OldTenants';
import Complaints from './components/manager/Complaints';
import FoodMess from './components/manager/FoodMess';
import CleaningChecklist from './components/manager/CleaningChecklist';
import Assets from './components/manager/Assets';
import Listings from './components/manager/Listings';
import Electricity from './components/manager/Electricity';
import NoticeBoard from './components/manager/NoticeBoard';
import VisitorLog from './components/manager/VisitorLog';
import Wallet from './components/manager/Wallet';
import Invoices from './components/manager/Invoices';
import { PublicListingsIndex, PublicListingDetail } from './components/public/PublicListings';
import TenantOnboard from './components/public/TenantOnboard';
import { PrivacyPolicy, TermsOfService, DataDeletion, RefundPolicy } from './components/public/LegalPages';
import ContactUs from './components/public/ContactUs';
import FeaturePage from './components/FeaturePage';
import FeaturesIndex from './components/FeaturesIndex';
import DocumentsVault from './components/manager/DocumentsVault';
import PageMeta from './components/PageMeta';
import LeadCaptureModal from './components/LeadCaptureModal';
import StickyCTA from './components/StickyCTA';
import GaRouteGuard from './components/GaRouteGuard';
import axios from 'axios';
import { initMixpanel, startSessionTimer, bindAxiosErrorTracker, bindCrossTabTracker } from './services/analytics';

initMixpanel();
startSessionTimer();
bindAxiosErrorTracker(axios);
bindCrossTabTracker();

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/manager/login" />;
};

// Public Landing Page
const LandingPage = () => {
  return (
    <>
      <PageMeta
        title="RentKollect — WhatsApp-first PG & Hostel management software"
        description="Manage your PG, hostel or co-living entirely on WhatsApp. Log rent, track expenses with bill photos, onboard tenants, get daily digests. Built for Indian PG owners. Free to start."
        suffix={false}
        canonical="https://rentkollect.com/"
      />
      <Navbar />
      <HeroSection />
      <FeaturesSection />
      <ComparisonSection />
      <FAQSection />
      <Footer />
      <LeadCaptureModal />
      <StickyCTA />
    </>
  );
};

// Wraps other public pages (listings, legal) with lead-capture widgets.
const PublicShell = ({ children }) => (
  <>
    {children}
    <LeadCaptureModal />
    <StickyCTA />
  </>
);

// Placeholder components for manager routes
const ComingSoon = ({ title }) => (
  <div className="p-6">
    <PageMeta title={`${title} (coming soon)`} description={`${title} module is under development on RentKollect — coming soon.`} noindex />
    <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
      <h2 className="text-3xl font-bold text-gray-900 mb-4">{title}</h2>
      <p className="text-gray-600 mb-6">This feature is under development and will be available soon.</p>
      <div className="inline-block bg-blue-100 text-blue-800 px-6 py-3 rounded-full font-medium">
        Coming in Phase 2
      </div>
    </div>
  </div>
);

// Legacy URL bridge — older builds linked /manager/property/<id> (singular).
// The canonical route now lives at /manager/properties/:propertyId (plural).
// This silent redirect prevents any stale bookmark / shared link from showing
// a blank screen.
const RedirectToPluralProperty = () => {
  const { propertyId } = useParams();
  return <Navigate to={`/manager/properties/${propertyId}`} replace />;
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
          <GaRouteGuard />
          <ErrorBoundary>
          <Routes>
            {/* Public Routes — all get the LeadCaptureModal + StickyCTA */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/listings" element={<PublicShell><PublicListingsIndex /></PublicShell>} />
            <Route path="/listings/:propertyId" element={<PublicShell><PublicListingDetail /></PublicShell>} />
            <Route path="/tenant/onboard/:token" element={<TenantOnboard />} />
            <Route path="/privacy" element={<PublicShell><PrivacyPolicy /></PublicShell>} />
            <Route path="/terms" element={<PublicShell><TermsOfService /></PublicShell>} />
            <Route path="/data-deletion" element={<PublicShell><DataDeletion /></PublicShell>} />
            <Route path="/refund" element={<PublicShell><RefundPolicy /></PublicShell>} />
            <Route path="/contact" element={<PublicShell><ContactUs /></PublicShell>} />
            <Route path="/features" element={<PublicShell><FeaturesIndex /></PublicShell>} />
            <Route path="/features/:slug" element={<PublicShell><FeaturePage /></PublicShell>} />
            
            {/* Manager App Routes */}
            <Route path="/manager/login" element={<Login />} />
            
            <Route path="/manager" element={
              <ProtectedRoute>
                <ManagerLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/manager/home" />} />
              <Route path="home" element={<Home />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="money" element={<MoneyManagement />} />
              <Route path="money/collections" element={<Collections />} />
              <Route path="money/dues" element={<Dues />} />
              <Route path="money/expenses" element={<ExpensesList />} />
              <Route path="money/banks" element={<BanksList />} />
              <Route path="money/profit-loss" element={<ProfitLoss />} />
              <Route path="money/dues-packages" element={<DuesPackages />} />
              <Route path="people" element={<Navigate to="/manager/people/tenants" />} />
              <Route path="people/tenants" element={<TenantsList />} />
              <Route path="tenants/:tenantId/profile" element={<TenantProfile />} />
              <Route path="people/leads" element={<LeadsList />} />
              <Route path="people/bookings" element={<BookingsList />} />
              <Route path="people/team" element={<TeamManagement />} />
              <Route path="people/old-tenants" element={<OldTenants />} />
              <Route path="complaints" element={<Complaints />} />
              <Route path="food" element={<FoodMess />} />
              <Route path="cleaning" element={<CleaningChecklist />} />
              <Route path="assets" element={<Assets />} />
              <Route path="listings" element={<Listings />} />
              <Route path="property" element={<Navigate to="/manager/property/list" />} />
              <Route path="property/list" element={<PropertiesList />} />
              <Route path="properties/:propertyId" element={<PropertyDetails />} />
              {/* Legacy singular URL — keep redirect for old bookmarks / shared links */}
              <Route path="property/:propertyId" element={<RedirectToPluralProperty />} />
              <Route path="property/rooms" element={<RoomsList />} />
              <Route path="tasks" element={<TasksManagement />} />
              <Route path="reports" element={<Reports />} />
              <Route path="agreements" element={<RentAgreements />} />
              <Route path="police-verification" element={<PoliceVerifications />} />
              <Route path="reminders" element={<Reminders />} />
              <Route path="reminders/settings" element={<ReminderSettings />} />
              <Route path="listings" element={<ComingSoon title="Listings" />} />
              <Route path="electricity" element={<Electricity />} />
              <Route path="notice-board" element={<NoticeBoard />} />
              <Route path="visitors" element={<VisitorLog />} />
              <Route path="wallet" element={<Wallet />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="documents" element={<DocumentsVault />} />
              <Route path="banks" element={<ComingSoon title="Banks" />} />
              <Route path="flexpe" element={<ComingSoon title="FlexPe" />} />
              <Route path="whatsapp" element={<ComingSoon title="WhatsApp" />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            {/* Super Admin App Routes */}
            <Route path="/admin/login"   element={<AdminLogin />} />
            <Route path="/admin/*"       element={<AdminLayout />} />
            <Route path="/admin"         element={<Navigate to="/admin/dashboard" />} />

            {/* Catch-all — any URL that didn't match any registered route lands here
                instead of rendering a silent blank screen. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </ErrorBoundary>
          <Toaster />
          </BrowserRouter>
        </div>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
